import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Search } from "lucide-react";
import { US_JURISDICTIONS } from "@/lib/states";
import { useI18n } from "@/context/I18nContext";

const FIELDS = [
  ["first_name", "home.firstName", "search-first-name", "e.g. John"],
  ["last_name", "home.lastName", "search-last-name", "e.g. Smith"],
  ["middle_name", "home.middleName", "search-middle-name", null],
  ["inmate_id", "home.inmateId", "search-inmate-id", "e.g. 12345-678"],
];

const Label = ({ children }) => (
  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">{children}</label>
);

export const SearchForm = ({ initial = {}, onSubmitted }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [v, setV] = useState({ first_name: "", last_name: "", middle_name: "", inmate_id: "", state: "", dob: "", ...initial });
  const set = (k) => (val) => setV((p) => ({ ...p, [k]: val }));

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    Object.entries(v).forEach(([k, val]) => { if (val && val !== "__any__") params.set(k, val); });
    navigate(`/search?${params.toString()}`);
    onSubmitted?.();
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <form onSubmit={submit} className="p-5 sm:p-6" data-testid="prisoner-search-form">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FIELDS.map(([k, label, tid, ph]) => (
            <div key={k}>
              <Label>{t(label)}</Label>
              <Input data-testid={tid} value={v[k]} onChange={(e) => set(k)(e.target.value)} placeholder={ph || t("home.optional")} />
            </div>
          ))}
          <div>
            <Label>{t("home.jurisdiction")}</Label>
            <Select value={v.state} onValueChange={set("state")}>
              <SelectTrigger data-testid="search-state-select"><SelectValue placeholder={t("home.anyJurisdiction")} /></SelectTrigger>
              <SelectContent>
                {US_JURISDICTIONS.map(([val, l]) => (
                  <SelectItem key={val || "any"} value={val || "__any__"}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("home.dob")}</Label>
            <Input data-testid="search-dob" type="date" value={v.dob} onChange={(e) => set("dob")(e.target.value)} />
          </div>
        </div>
        <div className="mt-5 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <p className="text-xs text-slate-500 leading-relaxed max-w-xl">{t("home.sameNameNote")}</p>
          <Button type="submit" data-testid="search-submit-btn" size="lg" className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-6">
            <Search size={16} className="mr-2" /> {t("home.submit")}
          </Button>
        </div>
      </form>
    </Card>
  );
};
